import {
  buildDeniedAccessSnapshotFromRoles,
  buildAccessSnapshotFromRoles,
  getManifestEntry,
  getPageKeysForModuleKey,
  hasFullAccessRole,
  isPermissionRoleFallbackEnabled,
  MODULE_KEYS,
  PAGE_KEYS,
} from "@/config/permissionManifest";
import {
  CRM_PHONE_BLOCKED_ROLES,
  GUARDIAN_PHONE_BLOCKED_ROLES,
  STAFF_PHONE_BLOCKED_ROLES,
} from "@/config/sensitiveContact";
import { isOffline } from "@/services/offlineSignal";
import type { AccessSnapshot } from "@/type/permissions";

const PERMISSION_KEY = "user_permissions";
const ACCESS_SNAPSHOT_KEY = "alkokh_access_snapshot";

/**
 * How long a persisted access snapshot may be trusted before it must be
 * re-fetched from the backend.
 *
 * Until this existed the snapshot had NO expiry: `saveAccessSnapshot` wrote
 * `loadedAt` and nothing ever read it, so a snapshot in `localStorage` survived
 * tab closes, browser restarts and reboots — indefinitely, until an explicit
 * logout. On shared clinic workstations that nobody logs out of, that means a
 * snapshot can be months old and still be granting page access the backend will
 * refuse.
 *
 * 8 hours covers one continuous shift, so nobody is disturbed mid-shift, while
 * guaranteeing a snapshot cannot survive overnight into the next shift on a
 * shared machine. Expiry is invisible when the backend is healthy: it makes
 * `getAccessSnapshot` return null, the router stops latching and re-fetches, and
 * `get_current_access` answers in ~0.13s.
 */
const ACCESS_SNAPSHOT_TTL_MS = 8 * 60 * 60 * 1000;

// Parsing `loadedAt` out of the raw JSON is memoized on the raw string (which is
// stable), but the staleness COMPARISON must run per call — see the note in
// `getAccessSnapshot` about why this cannot live behind the value memo.
let loadedAtCache: { raw: string; at: number } | null = null;

function snapshotLoadedAt(rawSnapshot: string): number {
  if (loadedAtCache?.raw === rawSnapshot)
    return loadedAtCache.at;

  const parsed = safeParseJson<AccessSnapshot>(rawSnapshot);
  const at = Date.parse(parsed?.loadedAt ?? "");

  loadedAtCache = { raw: rawSnapshot, at };

  return at;
}

function isRawSnapshotStale(rawSnapshot: string): boolean {
  const at = snapshotLoadedAt(rawSnapshot);

  // A snapshot with no parseable timestamp predates the TTL and cannot be
  // vouched for — treat it as stale so it is re-fetched once.
  if (!Number.isFinite(at))
    return true;

  return Date.now() - at > ACCESS_SNAPSHOT_TTL_MS;
}

/**
 * The navigation menu resolves permissions once per nav node (~80-100 nodes)
 * on every render/route change, and each resolution used to re-read localStorage
 * and re-run JSON.parse + Set/unique normalization. That synchronous churn made
 * the sidebar laggy. We memoize the parsed/normalized snapshots keyed on the raw
 * localStorage strings, so repeated reads are free and the cache self-invalidates
 * the moment the stored permissions actually change (including from another tab).
 */
type SnapshotCache<T> = {
  rawSnapshot: string | null;
  rawPermissions: string | null;
  value: T;
};

let accessSnapshotCache: SnapshotCache<AccessSnapshot | null> | null = null;
let orBuildSnapshotCache: SnapshotCache<AccessSnapshot> | null = null;

function isSameCache<T>(
  cache: SnapshotCache<T> | null,
  rawSnapshot: string | null,
  rawPermissions: string | null,
): cache is SnapshotCache<T> {
  return (
    !!cache
    && cache.rawSnapshot === rawSnapshot
    && cache.rawPermissions === rawPermissions
  );
}

function safeParseJson<T>(value: string | null): T | null {
  if (!value)
    return null;

  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

function unique(values: string[]) {
  return [...new Set(values.filter(Boolean))];
}

function getDoctypePermissions(doctypes: AccessSnapshot["doctypes"], doctype: string) {
  const exact = doctypes?.[doctype];
  if (exact)
    return exact;

  const normalizedDoctype = doctype.toLowerCase();
  const matchedKey = Object.keys(doctypes ?? {})
    .find(key => key.trim().toLowerCase() === normalizedDoctype);

  return matchedKey ? doctypes?.[matchedKey] : undefined;
}

function normalizePermissionValue(value: any): string {
  if (typeof value === "string")
    return value.trim();

  return String(
    value?.role
    || value?.name
    || value?.role_profile
    || value?.roleProfile
    || value?.role_profile_name
    || "",
  ).trim();
}

function normalizePermissions(perms: any): string[] {
  const source = Array.isArray(perms)
    ? perms
    : Array.isArray(perms?.message)
      ? perms.message
      : Array.isArray(perms?.data)
        ? perms.data
        : Array.isArray(perms?.roles)
          ? perms.roles
          : [];

  return unique(
    source
      .map(normalizePermissionValue)
      .filter(Boolean),
  );
}

export function savePermissions(perms: { message: string[] } | string[] | any) {
  const normalizedPermissions = normalizePermissions(perms);

  localStorage.setItem(PERMISSION_KEY, JSON.stringify(normalizedPermissions));

  return normalizedPermissions;
}

export function getPermissions(): string[] {
  const raw = localStorage.getItem(PERMISSION_KEY);
  const parsed = safeParseJson<any[]>(raw);

  return normalizePermissions(parsed);
}

function mergeStoredRoles(roles: string[]) {
  return unique([
    ...roles.map(normalizePermissionValue),
    ...getPermissions(),
  ]);
}

export function saveAccessSnapshot(snapshot: AccessSnapshot | null) {
  if (!snapshot) {
    localStorage.removeItem(ACCESS_SNAPSHOT_KEY);

    return null;
  }

  const normalizedSnapshot: AccessSnapshot = {
    ...snapshot,
    roles: mergeStoredRoles(snapshot.roles ?? []),
    modules: unique(snapshot.modules ?? []),
    pages: unique(snapshot.pages ?? []),
    actions: unique(snapshot.actions ?? []),
    doctypes: snapshot.doctypes ?? {},
    restrictions: snapshot.restrictions ?? {},
    loadedAt: snapshot.loadedAt ?? new Date().toISOString(),
  };

  localStorage.setItem(ACCESS_SNAPSHOT_KEY, JSON.stringify(normalizedSnapshot));

  return normalizedSnapshot;
}

export function getAccessSnapshot(): AccessSnapshot | null {
  const rawSnapshot = localStorage.getItem(ACCESS_SNAPSHOT_KEY);
  const rawPermissions = localStorage.getItem(PERMISSION_KEY);

  // Staleness is evaluated BEFORE the memo, and deliberately not memoized:
  // the value cache is keyed on the raw strings, which do not change as time
  // passes, so a snapshot memoized while fresh would keep being served forever
  // after it expired. Purge it too, so a stale snapshot cannot linger in
  // storage and be picked up by a later `getOrBuildAccessSnapshot` call.
  //
  // ⚠️ EXCEPT WHEN THE BACKEND CANNOT BE REACHED. Expiry assumes a refetch is
  // one call away; offline there is no refetch, and deleting the snapshot turns
  // every page into a denial. With the role fallback off (it is, in every env
  // file) `getOrBuildAccessSnapshot` then returns a zero-page DENIED snapshot,
  // `canPage` is false for all non-admins, and the router sends staff to
  // /not-authorized with no way back until the network returns — on a device
  // that may be holding a day of unsent sales. So offline we keep serving the
  // stale snapshot and let the backend correct us on reconnect: it remains
  // authoritative for every real request, which is where a revoked permission
  // is actually enforced.
  if (rawSnapshot && isRawSnapshotStale(rawSnapshot)) {
    if (isOffline()) {
      console.warn("Access snapshot is past its TTL but the server is unreachable; serving it as stale-but-usable and re-verifying on reconnect");
    }
    else {
      console.warn("Access snapshot expired; clearing and re-fetching from backend");
      clearAccessSnapshot();

      return null;
    }
  }

  if (isSameCache(accessSnapshotCache, rawSnapshot, rawPermissions))
    return accessSnapshotCache.value;

  const snapshot = safeParseJson<AccessSnapshot>(rawSnapshot);

  const value: AccessSnapshot | null = snapshot
    ? {
        ...snapshot,
        roles: mergeStoredRoles(snapshot.roles ?? []),
        modules: unique(snapshot.modules ?? []),
        pages: unique(snapshot.pages ?? []),
        actions: unique(snapshot.actions ?? []),
        doctypes: snapshot.doctypes ?? {},
        restrictions: snapshot.restrictions ?? {},
      }
    : null;

  accessSnapshotCache = { rawSnapshot, rawPermissions, value };

  return value;
}

export function getOrBuildAccessSnapshot(): AccessSnapshot {
  const rawSnapshot = localStorage.getItem(ACCESS_SNAPSHOT_KEY);
  const rawPermissions = localStorage.getItem(PERMISSION_KEY);

  if (isSameCache(orBuildSnapshotCache, rawSnapshot, rawPermissions))
    return orBuildSnapshotCache.value;

  const snapshot = getAccessSnapshot();

  let value: AccessSnapshot;
  if (snapshot) {
    value = snapshot;
  }
  else {
    const roles = mergeStoredRoles([]);
    value = isPermissionRoleFallbackEnabled()
      ? buildAccessSnapshotFromRoles(roles)
      : buildDeniedAccessSnapshotFromRoles(roles);
  }

  orBuildSnapshotCache = { rawSnapshot, rawPermissions, value };

  return value;
}

export function clearPermissions() {
  localStorage.removeItem(PERMISSION_KEY);
  localStorage.removeItem(ACCESS_SNAPSHOT_KEY);
}

export function clearAccessSnapshot() {
  localStorage.removeItem(ACCESS_SNAPSHOT_KEY);
}

export function canRole(role: string): boolean {
  const roles = getOrBuildAccessSnapshot().roles;

  return roles.includes(role) || hasFullAccessRole(roles);
}

export function canAnyRole(rolesToCheck: string[]): boolean {
  const roles = getOrBuildAccessSnapshot().roles;

  return hasFullAccessRole(roles) || rolesToCheck.some(role => roles.includes(role));
}

function canLegacyResource(key: string, bucket: "modules" | "pages") {
  const snapshot = getOrBuildAccessSnapshot();

  if (hasFullAccessRole(snapshot.roles))
    return true;

  const resources = snapshot[bucket] ?? [];

  if (resources.includes(key))
    return true;

  if (snapshot.source !== "roles-fallback" || !isPermissionRoleFallbackEnabled())
    return false;

  const entry = getManifestEntry(key);
  const fallbackRoles = entry?.fallbackRoles ?? [];

  if (fallbackRoles.some(role => snapshot.roles.includes(role)))
    return true;

  return false;
}

const FRONTEND_REGISTERED_ROLE_FALLBACK_MODULES = new Set<string>([
  MODULE_KEYS.CRM,
  MODULE_KEYS.REPORT_ENGINE,
]);

function canFrontendRegisteredFallback(key: string, snapshot: AccessSnapshot) {
  const entry = getManifestEntry(key);
  const moduleKey = key.startsWith("module.")
    ? key
    : "moduleKey" in (entry ?? {})
      ? String((entry as { moduleKey?: string }).moduleKey ?? "")
      : "";

  if (!FRONTEND_REGISTERED_ROLE_FALLBACK_MODULES.has(moduleKey))
    return false;

  if (hasFullAccessRole(snapshot.roles))
    return true;

  const fallbackRoles = entry?.fallbackRoles ?? [];

  return fallbackRoles.some(role => snapshot.roles.includes(role));
}

function shouldUseFullAccessBootstrapFallback(snapshot: AccessSnapshot) {
  return snapshot.source === "backend"
    && hasFullAccessRole(snapshot.roles)
    && !(snapshot.pages ?? []).length;
}

export function canModule(key: string): boolean {
  const snapshot = getOrBuildAccessSnapshot();

  if (shouldUseFullAccessBootstrapFallback(snapshot))
    return true;

  const pageKeys = getPageKeysForModuleKey(key);
  if (pageKeys.some(pageKey => (snapshot.pages ?? []).includes(pageKey)))
    return true;

  if (snapshot.source === "backend")
    return false;

  if (canFrontendRegisteredFallback(key, snapshot))
    return true;

  return canLegacyResource(key, "modules");
}

export function canPage(key: string): boolean {
  const snapshot = getOrBuildAccessSnapshot();

  if (shouldUseFullAccessBootstrapFallback(snapshot))
    return true;

  if ((snapshot.pages ?? []).includes(key))
    return true;

  if (snapshot.source === "backend")
    return false;

  if (canFrontendRegisteredFallback(key, snapshot))
    return true;

  return canLegacyResource(key, "pages");
}

export function canAction(key: string): boolean {
  console.warn(`Ignoring legacy action permission key "${key}". Use canDoctype(doctype, permission) for UI visibility.`);

  return false;
}

export function canDoctype(doctype: string, action: string): boolean {
  const normalizedDoctype = String(doctype ?? "").trim();
  const normalizedAction = String(action ?? "").trim().toLowerCase();

  if (!normalizedDoctype || !normalizedAction)
    return false;

  const snapshot = getOrBuildAccessSnapshot();

  if (hasFullAccessRole(snapshot.roles))
    return true;

  const permissions = getDoctypePermissions(snapshot.doctypes, normalizedDoctype);

  if (Array.isArray(permissions)) {
    return permissions
      .some(item => String(item).trim().toLowerCase() === normalizedAction);
  }

  if (permissions && typeof permissions === "object") {
    const value = (permissions as Record<string, unknown>)[normalizedAction];

    return value === true || value === 1 || value === "1" || String(value).toLowerCase() === "true";
  }

  return false;
}

export function canAccessResource(key: string, fallbackRoles?: string | string[]): boolean {
  if (!key)
    return true;

  if (key.startsWith("module."))
    return canModule(key);

  if (key.startsWith("page."))
    return canPage(key);

  if (key.startsWith("action."))
    return false;

  const entry = getManifestEntry(key);
  const snapshot = getOrBuildAccessSnapshot();

  if (hasFullAccessRole(snapshot.roles))
    return true;

  if (
    snapshot.source === "roles-fallback"
    && isPermissionRoleFallbackEnabled()
    && entry?.fallbackRoles?.length
  )
    return canAnyRole(entry.fallbackRoles);

  if (
    snapshot.source === "roles-fallback"
    && isPermissionRoleFallbackEnabled()
    && fallbackRoles
  )
    return hasPermissions(fallbackRoles);

  return false;
}

/**
 * Checks if the user has at least one of the given backend roles.
 * This remains available as a migration fallback while the app moves to
 * module/page/action resource keys.
 */
export function hasPermissions(permissionOrArray: string | string[]): boolean {
  if (typeof permissionOrArray === "string")
    return canRole(permissionOrArray);

  if (Array.isArray(permissionOrArray))
    return canAnyRole(permissionOrArray);

  return false;
}

export function hasPermission(permission: string): boolean {
  return hasPermissions(permission);
}

export function hasAnyPermission(permissions: string[]): boolean {
  return canAnyRole(permissions);
}

export function hasAllPermissions(permissions: string[]): boolean {
  const roles = getOrBuildAccessSnapshot().roles;

  if (hasFullAccessRole(roles))
    return true;

  return permissions.every(permission => roles.includes(permission));
}

export function canViewAdminMoney(): boolean {
  return hasFullAccessRole(getOrBuildAccessSnapshot().roles);
}

/**
 * Sensitive-phone visibility (guardian / staff / CRM). Blocklist semantics with a
 * FAIL-CLOSED guard: when the access snapshot is not yet resolved — still loading
 * (router racing `get_current_access` against ACCESS_LOAD_TIMEOUT_MS), fresh or
 * cleared storage, or a denied snapshot — we HIDE. Unknown never means shown.
 *
 * Resolved  = `source` is "backend"/"roles-fallback" AND at least one role.
 * Unresolved = `source === "empty"` OR no roles → hide.
 *
 * NOTE: `canAnyRole` is deliberately NOT reused — it grants full-access `true`
 * (wrong for a blocklist) and does not fail closed.
 */
function canViewSensitivePhone(blockedRoles: readonly string[]): boolean {
  const snapshot = getOrBuildAccessSnapshot();

  // Fail closed: hide until we positively know the user's roles.
  if (snapshot.source === "empty" || (snapshot.roles?.length ?? 0) === 0)
    return false;

  if (hasFullAccessRole(snapshot.roles))
    return true;

  return !snapshot.roles.some(role => blockedRoles.includes(role));
}

export function canViewGuardianPhone(): boolean {
  return canViewSensitivePhone(GUARDIAN_PHONE_BLOCKED_ROLES);
}

export function canViewStaffPhone(): boolean {
  return canViewSensitivePhone(STAFF_PHONE_BLOCKED_ROLES);
}

export function canViewCrmPhone(): boolean {
  return canViewSensitivePhone(CRM_PHONE_BLOCKED_ROLES);
}

/**
 * Redaction helpers for MAPPERS (API-response → frontend state). They blank the
 * phone to "" when the current user is blocked (or access is unresolved), so every
 * display site's existing empty-handling ("-" / v-if / emptyText) renders the
 * hidden state automatically — no per-site edits.
 *
 * ⚠️ These blank the value in STATE, not just on screen. Guardian phone is also
 * consumed for LOGIC (WhatsApp matching/sending) and PERSISTENCE (case-sheet
 * creation). While the blocklists are empty this is inert. Before populating
 * GUARDIAN_PHONE_BLOCKED_ROLES, route those consumers through an un-redacted
 * field — see docs/AGENT.md, "Sensitive phone visibility".
 */
export function redactGuardianPhone(raw?: string | null): string {
  return canViewGuardianPhone() ? (raw ? String(raw) : "") : "";
}

export function redactStaffPhone(raw?: string | null): string {
  return canViewStaffPhone() ? (raw ? String(raw) : "") : "";
}

export function redactCrmPhone(raw?: string | null): string {
  return canViewCrmPhone() ? (raw ? String(raw) : "") : "";
}

export function canViewRatings(): boolean {
  return canDoctype("Rating", "read");
}

export function canSubmitRatings(): boolean {
  return canDoctype("Rating", "create");
}

export function canManageRestrictedClinicalAttachments(): boolean {
  // Overriding the release lock to delete attachments on released/terminal
  // clinical records is a full-access-only capability (Administrator, System
  // Manager, Pet App Admin). It must NOT be available to routine clinical
  // updaters, so this deliberately does not map to Lab/Imaging "write".
  return hasFullAccessRole(getOrBuildAccessSnapshot().roles);
}

export function canManageReleasedLabAttachments(): boolean {
  return canManageRestrictedClinicalAttachments();
}

// Home/profile and the (page-less) module launcher are granted to every desk
// user, so they are not "real" destinations for the reception-lock decision.
const RECEPTION_LOCK_IGNORED_PAGE_KEYS = new Set<string>([
  PAGE_KEYS.ROOT,
  PAGE_KEYS.MODULES,
]);

/**
 * True when the signed-in user should be locked to the chrome-free clinic intake
 * kiosk (/clinic-station): the Clinic Station page is the ONLY real destination
 * their access snapshot grants (home/profile don't count) and they are not a
 * full-access admin. Under the new permission model "Clinic Reception" is simply
 * a Role Profile that holds only the `Clinic Station Page` role — no role name is
 * referenced here. Used by the post-login redirect, the navigation guard, and the
 * sidebar filter.
 */
export function isClinicReceptionOnlyUser(): boolean {
  const snapshot = getOrBuildAccessSnapshot();

  if (hasFullAccessRole(snapshot.roles))
    return false;

  const pages = snapshot.pages ?? [];

  if (!pages.includes(PAGE_KEYS.CLINIC_STATION))
    return false;

  const otherRealPages = pages.filter(
    page => page !== PAGE_KEYS.CLINIC_STATION && !RECEPTION_LOCK_IGNORED_PAGE_KEYS.has(page),
  );

  return otherRealPages.length === 0;
}
